import { Box, Button, CheckboxInput, Modal, ModalProps } from '@palmetto/palmetto-components';
import { useCallback, useRef, useState } from 'react';

import { AttestmentData } from 'types';

export interface AttestmentModalProps extends ModalProps {
  onAttestmentConfirmed: (data: AttestmentData) => Promise<void>;
  labels: string[];
  title: string;
  isLoading: boolean;
}

/**
 * @description This is the confirmation modal shown to attest certain data
 */
export function AttestmentModal({
  isOpen,
  onDismiss,
  onAttestmentConfirmed,
  labels,
  title,
  isLoading,
}: AttestmentModalProps) {
  const [checked, setChecked] = useState(false);
  // isLoading only disables Proceed after a re-render, so a fast double click would fire two updates.
  const isConfirmInFlight = useRef(false);

  const handleProceed = useCallback(async () => {
    if (isConfirmInFlight.current) return;
    isConfirmInFlight.current = true;
    try {
      await onAttestmentConfirmed({ attestmentText: labels.join('\n') });
    } finally {
      isConfirmInFlight.current = false;
    }
  }, [onAttestmentConfirmed, labels]);

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxWidth="4xl" ariaLabelledBy="confirmContractVoiding">
      <Modal.Header id="confirmContractVoiding" title={title} onDismiss={onDismiss} />
      <Modal.Body background="secondary" childGap="lg">
        <Box
          fontSize={'sm'}
          color="body-primary"
          data-testid="void-confirmation-modal-body"
          justifyContent={'center'}
          alignItems={'center'}
          display={'flex'}
          direction={'row'}
          childGap="sm"
        >
          <CheckboxInput
            id="attestmendModalCheckbox"
            label=""
            onChange={() => setChecked(!checked)}
            isChecked={checked}
            isDisabled={isLoading}
          />
          <Box display="flex" direction="column" childGap="sm">
            {labels.map((line, index) => (
              <Box key={index}>{line}</Box>
            ))}
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          tone="neutral"
          onClick={onDismiss}
          id="attestmendModalCancelCTA"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          tone="primary"
          id="attestmendModalProceedCTA"
          isLoading={isLoading}
          isDisabled={isLoading || !checked}
          onClick={handleProceed}
        >
          Proceed
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
